import { Controller } from "@hotwired/stimulus";
import { Loader } from "@googlemaps/js-api-loader";

/**
 * Google Mapを表示する
 * @example
 * <div
 *   data-controller="map"
 *   data-map-apikey-value="YOUR_API_KEY"
 * >
 *  <select data-map-target="prefecture"></select>
 *  <input data-map-target="address" type="text" placeholder="市区町村">
 *  <input data-map-target="building" type="text" placeholder="建物名">
 *
 *  <div data-map-target="map">ここに地図</div>
 *
 *  選択した緯度: <input data-map-target="lat" type="hidden">
 *  選択した経度: <input data-map-target="lng" type="hidden">
 * </div>
 */
export class MapController extends Controller {
  static values = {
    apiKey: String,
    // デフォルト値は渋谷駅
    lat: { type: Number, default: 35.65822129419941 },
    lng: { type: Number, default: 139.70163579634163 },
    zoom: { type: Number, default: 16 },
    mapId: { type: String, default: "MyMap" },
  };

  static targets = [
    // 地図を表示する要素
    "map",
    // 選んだ緯度経度を格納するinput
    "lat",
    "lng",
    // 住所入力
    "prefecture",
    "address",
    "building",
  ];

  async connect() {
    if (!this.apiKeyValue) {
      console.error("data-map-apikey-value is required.");
      return;
    }

    this.#initializeLatLng();

    // Google Maps を読みこむ
    const loader = new Loader({
      apiKey: this.apiKeyValue,
      version: "weekly",
    });

    const { Map } = await loader.importLibrary("maps");
    this.#initializeMap(Map);

    const { AdvancedMarkerElement } = await loader.importLibrary("marker");
    this.#initializeMarker(AdvancedMarkerElement);

    const { Geocoder } = await loader.importLibrary("geocoding");
    this.geocoder = new Geocoder();

    // 入力をやっつける
    this.#addInputListeners();
  }

  // 緯度と経度の入力があれば value に入れる。無ければ逆。
  #initializeLatLng() {
    if (this.hasLatTarget && this.latTarget.value && this.hasLngTarget && this.lngTarget.value) {
      this.latValue = this.latTarget.value;
      this.lngValue = this.lngTarget.value;
    } else {
      this.#updateLatLngInput({ lat: this.latValue, lng: this.lngValue });
    }
  }

  // 地図を初期化
  #initializeMap(Map) {
    const element = this.hasMapTarget ? this.mapTarget : this.element;
    this.map = new Map(element, {
      center: { lat: this.latValue, lng: this.lngValue },
      zoom: this.zoomValue,
      mapId: this.mapIdValue,
      streetViewControl: false,
      mapTypeControl: false,
    });
  }

  // マーカーを初期化
  #initializeMarker(AdvancedMarkerElement) {
    window.marker = this.marker = new AdvancedMarkerElement({
      position: { lat: this.latValue, lng: this.lngValue },
      map: this.map,
      gmpDraggable: true,
    });
    // マーカーのドラッグ終了時に緯度経度を更新
    this.marker.addListener("dragend", () => {
      console.debug("dragend", this.marker.position);
      this.map.setCenter(this.marker.position);
      this.#updateLatLngInput(this.marker.position);
    });
  }

  // 住所入力時に地図の中心とマーカーを移動
  #moveTo({ lat, lng }) {
    console.debug("moveTo", lat, lng);
    this.map.setCenter({ lat, lng });
    this.marker.position = { lat, lng };
    this.#updateLatLngInput({ lat, lng });
  }

  // 緯度経度inputを更新
  #updateLatLngInput({ lat, lng }) {
    if (this.hasLatTarget) {
      this.latTarget.value = lat;
    }
    if (this.hasLngTarget) {
      this.lngTarget.value = lng;
    }
  }

  // 住所入力時にジオコーダーを呼ぶ
  #addInputListeners() {
    // 都道府県
    if (this.hasPrefectureTarget) {
      this.#setPrefecture(this.prefectureTarget);
      this.prefectureTarget.addEventListener("change", (e) => {
        console.debug("change prefecture", e.currentTarget.value);
        this.#setPrefecture(e.currentTarget);
        this.#geocode();
      });
    }
    // 市区町村
    if (this.hasAddressTarget) {
      this.address = this.addressTarget.value;
      this.addressTarget.addEventListener("input", (e) => {
        console.debug("input address", e.currentTarget.value);
        this.address = e.currentTarget.value;
        this.#geocode();
      });
    }
  }

  // selectのラベルを都道府県にセット
  #setPrefecture(select) {
    // <select><option>選択せよ</option><option value="13">東京都</option>...</select>
    // みたいなのが来る
    const option = select.selectedOptions[0];
    if (option.value) {
      this.prefecture = option.text;
    } else {
      this.prefecture = "";
    }
  }

  // 入力された住所で位置を更新
  #geocode() {
    console.debug("geocode", this.prefecture, this.address);
    // いずれも入力されていない場合はスキップ
    if (!this.prefecture && !this.address.trim()) {
      return;
    }

    const address = `${this.prefecture} ${this.address}`;
    this.geocoder.geocode({ address }, (results, status) => {
      if (status === "OK") {
        const location = results[0].geometry.location;
        console.debug("Geocode was successful", results);
        this.#moveTo({ lat: location.lat(), lng: location.lng() });
      } else if (status !== "ZERO_RESULTS") {
        console.error("Geocode was not successful for the following reason: ", status);
      }
    });
  }
}
